import { createAuthServiceClient, type AuthServiceClient } from '@eis/authservice-sdk';
import {
  createInsuranceServiceClients,
  type ConfigClient,
  type InstanceClient,
} from '@eis/insuranceservice-sdk';
import type { TokenProvider } from '@eis/insuranceservice-sdk';
import { createPartnerServiceClient, type PartnerServiceClient } from '@eis/partnerservice-sdk';
import { createClaimServiceClient, type ClaimServiceClient } from '@eis/claimservice-sdk';
import { createBillingClient, type BillingClient } from '@eis/billingservice-sdk';
import { createDataServiceClient, type DataServiceClient } from '@eis/dataservice-sdk';

/**
 * The console's entire outbound surface (decision T-7).
 *
 * Every request to a service goes through one of these five clients — there is no second way,
 * and the lint wall in packages/config/eslint.config.js is there to keep it that way. What the
 * console gets for free by not having a second way: bearer auth from the live session, optimistic
 * concurrency with typed conflicts (T-12) and a traceparent per request (T-13), because those
 * live in the SDK runtime rather than at each call site.
 *
 * Base URLs are relative to `apiBaseUrl` from config.json, so the same bundle points at the
 * compose stack locally and at the real gateways in EKS without a rebuild (backbone §7).
 */
export interface EisApiClients {
  readonly auth: AuthServiceClient;
  /** Tarifs and Group Tarifs: immutable, pinned, lockfile semantics (architecture §A.2). */
  readonly config: ConfigClient;
  /** Policies and Group Policies: bitemporal, independent versions, resolve-at-read. */
  readonly instance: InstanceClient;
  /**
   * The Partner domain's own API (SDD-014): another team's service, reached through the same
   * gateway. No ETag middleware — the service is not party to that contract (PS-11).
   */
  readonly partners: PartnerServiceClient;
  /**
   * The billing service (SDD-015): invoices, bills, the schedule and the bill runs. Insert-only
   * documents carry no ETag; the schedule row and the runs are versioned and guarded (B-4, T-12).
   */
  readonly billing: BillingClient;
  /**
   * claimservice (SDD-018 §8): another team's image, run as is, reached through the same gateway.
   * No ETag middleware, for the Partner service's reason (`@eis/claimservice-sdk` README).
   */
  readonly claims: ClaimServiceClient;
  /**
   * The Data Suite's service (SDD-024 §6): reports, custom datasets, the catalog, queries and the
   * workspace settings. A platform service: heads and settings carry an ETag (T-12).
   */
  readonly data: DataServiceClient;
}

export interface ApiClientOptions {
  /** From config.json, e.g. `/api`. */
  readonly apiBaseUrl: string;
  /**
   * `adapter.getAccessToken` — called PER REQUEST, so a token refreshed by the auth adapter is
   * picked up without rebuilding a client, and a signed-out session simply stops sending one.
   */
  readonly tokenProvider: TokenProvider;
  /**
   * The auth adapter's refresh-now (SDD-019 S-6, S-11). Every client gets it, so a 401/403 is
   * refreshed and replayed once, and a dropped connection on a safe request is retried — the same
   * way for every service, whichever backend the adapter refreshes through.
   */
  readonly sessionRefresher?: () => Promise<string | null>;
  /** The adapter's "this window has no session any more". */
  readonly onSessionLost?: () => void;
  /** The adapter's own refresh endpoints, never retried. */
  readonly isSessionEndpoint?: (url: string) => boolean;
}

/**
 * `apiBaseUrl` is normally origin-relative (`/api`), which a browser resolves against the page
 * and Node does not resolve at all — `new Request('/api/...')` is an "Invalid URL" there. So the
 * origin is filled in when there is one, which is what the browser was going to do anyway and is
 * what lets the same clients run under jsdom in the hook tests.
 */
function absoluteBaseUrl(apiBaseUrl: string): string {
  const trimmed = apiBaseUrl.replace(/\/+$/, '');
  if (/^[a-z][a-z\d+\-.]*:/i.test(trimmed)) return trimmed;
  const origin: string | undefined = globalThis.location?.origin;
  return origin === undefined ? trimmed : `${origin}${trimmed}`;
}

export function createApiClients({
  apiBaseUrl,
  tokenProvider,
  sessionRefresher,
  onSessionLost,
  isSessionEndpoint,
}: ApiClientOptions): EisApiClients {
  const base = absoluteBaseUrl(apiBaseUrl);
  // The same session behaviour on every client (SDD-019 S-6).
  const session = {
    ...(sessionRefresher === undefined ? {} : { sessionRefresher }),
    ...(onSessionLost === undefined ? {} : { onSessionLost }),
    ...(isSessionEndpoint === undefined ? {} : { isSessionEndpoint }),
  };
  const insurance = createInsuranceServiceClients({
    baseUrl: `${base}/insurance`,
    tokenProvider,
    ...session,
  });
  return {
    auth: createAuthServiceClient({ baseUrl: `${base}/auth`, tokenProvider, ...session }),
    config: insurance.config,
    instance: insurance.instance,
    // Decided once, here: the Partner service's base is `${base}` and NOT `${base}/partnerservice`.
    // Its generated paths already begin with `/partnerservice/v1/…` (the document the Partner
    // domain published, vendored verbatim — PS-11), and Caddy strips only the `/api` prefix
    // before proxying (`handle /api/partnerservice/*` → `uri strip_prefix /api`, PS-15). So the
    // full request is `${base}/partnerservice/v1/partners`, exactly what MSW mirrors as well.
    partners: createPartnerServiceClient({ baseUrl: base, tokenProvider, ...session }),
    // The same decision for the billing service, made once more here: its base is `${base}`, its
    // generated paths already begin with `/billingservice/v1/…` (the service owns its legacy
    // prefix, SDD-015 B-1) and Caddy strips only `/api` (`handle /api/billingservice/*`). The full
    // request is `${base}/billingservice/v1/invoices`, exactly what MSW mirrors as well.
    billing: createBillingClient({ baseUrl: base, tokenProvider, ...session }),
    // claimservice, decided like the two above: `${base}`, its paths begin with `/claimservice/v1/…`
    // and Caddy strips only `/api` (`handle /api/claimservice/*`).
    claims: createClaimServiceClient({ baseUrl: base, tokenProvider, ...session }),
    // dataservice (SDD-024 A-4), decided like billing: `${base}`, its paths begin with
    // `/dataservice/v1/…` and Caddy strips only `/api` (`handle /api/dataservice/*`).
    data: createDataServiceClient({ baseUrl: base, tokenProvider, ...session }),
  };
}
